import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { Box, Button, Chip, IconButton, SxProps, TextField, Theme, Typography } from '@mui/material';
import { lengthRestrictions } from 'common/constants/length-restrictions';
import type { IPageFormV2 } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { DragEvent, FC, useEffect, useRef, useState } from 'react';
import {
  FieldArrayPath,
  FieldPath,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { styles } from './product-content-form.styles';

interface IProsBlockProps {
  productIndex: number;
}

export const ProsBlock: FC<IProsBlockProps> = ({ productIndex }) => {
  const {
    control,
    formState: { errors },
    getValues,
    register,
  } = useFormContext<IPageFormV2>();

  const LIMIT = lengthRestrictions.product.content.pros;
  const MIN_VISIBLE = 3;

  const sectionRef = useRef<HTMLDivElement>(null);

  const prosPath = `products.${productIndex}.content.pros` as FieldArrayPath<IPageFormV2>;

  const prosItems = useFieldArray({ control, name: prosPath });

  const contentObj = useWatch({
    control,
    name: `products.${productIndex}.content` as const,
  });

  const watchedPros = contentObj?.pros || [];

  const hasEmptyPros = watchedPros.some((pro) => !pro.value || pro.value.trim() === '');

  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    const formValues = getValues();
    const initialPros = formValues.products?.[productIndex]?.content?.pros || [];
    const hasInitialEmpty = initialPros.some((pro) => !pro.value || pro.value.trim() === '');

    return initialPros.length > MIN_VISIBLE && hasInitialEmpty;
  });

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    if (prosItems.fields.length > MIN_VISIBLE && hasEmptyPros) {
      setIsExpanded(true);
    }
  }, [prosItems.fields.length, hasEmptyPros]);

  const handleAdd = () => {
    prosItems.append({ value: '' });
    setIsExpanded(true);
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>, index: number) => {
    const formValues = getValues();
    const pro = formValues.products?.[productIndex]?.content?.pros?.[index];

    if (!pro?.value || pro.value.trim() === '') {
      e.preventDefault();
      return;
    }

    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setTimeout(() => {
      (e.target as HTMLElement).style.opacity = '0.4';
    }, 0);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    const formValues = getValues();
    const pro = formValues.products?.[productIndex]?.content?.pros?.[index];

    if (!pro?.value || pro.value.trim() === '') return;

    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();

    const formValues = getValues();
    const pro = formValues.products?.[productIndex]?.content?.pros?.[dropIndex];

    if (!pro?.value || pro.value.trim() === '') {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      prosItems.move(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = (e: DragEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleToggleExpand = () => {
    if (isExpanded && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsExpanded(!isExpanded);
  };

  const showOverlay = !isExpanded && prosItems.fields.length > MIN_VISIBLE;
  const visibleCount = isExpanded
    ? prosItems.fields.length
    : Math.min(prosItems.fields.length, MIN_VISIBLE);

  return (
    <Box ref={sectionRef} sx={{ scrollMarginTop: '120px' }}>
      <Box sx={styles.sectionTitle}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography fontWeight={600} variant="subtitle1">Product Pros / Cons</Typography>
          {prosItems.fields.length > 0 && (
            <Chip color="primary" label={prosItems.fields.length} size="small" variant="outlined" />
          )}
        </Box>
        <Button
          disabled={hasEmptyPros}
          onClick={handleAdd}
          size="small"
          startIcon={<AddOutlinedIcon />}
          variant="outlined"
        >
          Add Pros
        </Button>
      </Box>
      {hasEmptyPros && (
        <Typography color="error" sx={{ display: 'block', mb: 2 }} variant="caption">
          Please fill in empty Pros before adding a new one.
        </Typography>
      )}
      {prosItems.fields.length === 0 && (
        <Box sx={styles.emptyState}>
          <ThumbUpOutlinedIcon sx={{ fontSize: 36 }} />
          <Typography variant="body2">No pros / cons added yet</Typography>
        </Box>
      )}
      <Box sx={styles.gridWrapper}>
        <Box sx={styles.gridContainer}>
          {prosItems.fields.slice(0, visibleCount).map((elem, idx) => {
            const currentVal = watchedPros[idx]?.value || '';

            const err = errors?.products?.[productIndex]?.content?.pros?.[idx];
            const isError = !!err || currentVal.length > LIMIT;
            const isDragOver = dragOverIndex === idx;

            const inputPath =
              `products.${productIndex}.content.pros.${idx}.value` as FieldPath<IPageFormV2>;

            return (
              <Box
                key={elem.id}
                draggable
                onDragEnd={handleDragEnd}
                onDragEnter={(e) => handleDragEnter(e, idx)}
                onDragOver={(e) => e.preventDefault()}
                onDragStart={(e) => handleDragStart(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                sx={[styles.cardItem, isDragOver && styles.cardItemDragOver] as SxProps<Theme>}
              >
                <Box sx={styles.cardHeader}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={
                        [
                          styles.dragHandle,
                          {
                            cursor: !currentVal.trim() ? 'not-allowed' : 'grab',
                            opacity: !currentVal.trim() ? 0.3 : 1,
                          },
                        ] as SxProps<Theme>
                      }
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                    <Typography color="text.secondary" fontWeight="bold" variant="caption">
                      PROS #{idx + 1}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => prosItems.remove(idx)}
                    size="small"
                    sx={styles.deleteAction}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={styles.cardItemContent}>
                  <TextField
                    {...register(inputPath)}
                    error={isError}
                    fullWidth
                    helperText={err?.value?.message}
                    multiline
                    placeholder="Short text here..."
                    rows={3}
                    size="small"
                    slotProps={{ htmlInput: { maxLength: LIMIT } }}
                    sx={
                      [
                        styles.multilineInput,
                        { '& .MuiInputBase-root': { height: '80px' } },
                      ] as SxProps<Theme>
                    }
                  />
                  <Typography
                    sx={
                      [
                        styles.charCountSmall,
                        { color: currentVal.length > LIMIT ? 'error.main' : 'text.secondary' },
                      ] as SxProps<Theme>
                    }
                  >
                    {currentVal.length}/{LIMIT}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        {showOverlay && <Box sx={styles.gradientOverlay} />}
      </Box>
      {prosItems.fields.length > MIN_VISIBLE && (
        <Box sx={styles.expandButtonWrapper}>
          <Button
            endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            onClick={handleToggleExpand}
            sx={styles.expandButton}
          >
            {isExpanded ? 'Show Less' : `Show All (${prosItems.fields.length})`}
          </Button>
        </Box>
      )}
    </Box>
  );
};
